/**
 * LMS detection + the Canvas and Moodle presets (core bundle — detection must
 * run before anything else so the widget can decide whether to activate at all).
 *
 * Privacy: nothing in this module reads LMS user identifiers (Canvas
 * ENV.current_user, Moodle M.cfg user fields, names, emails). The only Canvas
 * ENV field read is `current_user_roles`; on Moodle only the presence of the
 * "Edit mode" switch / `editing` body class is checked. Both are used locally
 * (never sent anywhere) to decide whether a staff-only licensing notice may be shown.
 */

import type { ReadingVoxConfig } from './types/config';

export type LmsId = 'canvas' | 'd2l' | 'moodle' | 'blackboard';
export type PresetId = 'canvas' | 'moodle';

function safeHostname(): string {
  try { return (window.location.hostname || '').toLowerCase(); } catch { return ''; }
}

export function isCanvas(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (safeHostname().endsWith('.instructure.com')) return true;
  const env = (window as any).ENV;
  return !!env && typeof env === 'object' &&
    !!(document.querySelector('.ic-app') || document.getElementById('application'));
}

/** Moodle sets the global `M.cfg` (wwwroot, sesskey, …) on every page */
export function isMoodle(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const m = (window as any).M;
    return !!m && typeof m === 'object' && !!m.cfg && typeof m.cfg === 'object';
  } catch {
    return false;
  }
}

/** Preset to auto-apply on this page (website builds, no preset/selectors given) */
export function detectPreset(): PresetId | null {
  try {
    if (isCanvas()) return 'canvas';
    if (isMoodle()) return 'moodle';
  } catch { /* ignore */ }
  return null;
}

/** Best-effort LMS detection (reporting / licensing hint only) */
export function detectLMS(): LmsId | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  try {
    if (isCanvas()) return 'canvas';
    const w = window as any;
    if (w.D2L && typeof w.D2L === 'object') return 'd2l';
    if (isMoodle()) return 'moodle';
    if (safeHostname().includes('blackboard') || w.__BB || document.getElementById('globalNavPageNavArea')) {
      return 'blackboard';
    }
  } catch { /* ignore */ }
  return null;
}

/**
 * Canvas staff check for the licensing notice. Reads ONLY ENV.current_user_roles.
 */
export function isCanvasStaff(): boolean {
  try {
    const roles = (window as any).ENV?.current_user_roles;
    if (!Array.isArray(roles)) return false;
    return roles.some((r: unknown) => r === 'admin' || r === 'teacher' || r === 'root_admin');
  } catch {
    return false;
  }
}

/**
 * Moodle staff check for the licensing notice. Moodle exposes no role list to
 * the page, so use UI that only editors get: the `editing` body class (edit
 * mode on) or the navbar "Edit mode" switch. On course and activity pages that
 * switch requires moodle/course:update / manageactivities (teachers, managers,
 * admins). The Dashboard (/my/) shows it to students too, so it doesn't count
 * there — the notice is simply not shown on the Dashboard.
 */
export function isMoodleStaff(): boolean {
  try {
    const path = window.location.pathname || '';
    if (/\/my(\/|$)/i.test(path)) return false;
    if (!/\/(course|mod)\//i.test(path)) return false;
    if (document.body?.classList.contains('editing')) return true;
    return !!document.querySelector('form.editmode-switch-form, .editmode-switch-form input[name="setmode"]');
  } catch {
    return false;
  }
}

/**
 * Canvas pages where the widget must not activate at all: grades, gradebook /
 * SpeedGrader, inbox, people/users, settings, profile, account admin,
 * submission details, analytics, login, and editor pages (…/edit, …/new).
 * Matched against location.pathname (never the query string).
 */
export const CANVAS_EXCLUDED_PATHS: RegExp[] = [
  /\/(grades|gradebook|gradebooks|speed_grader|conversations|users|people|settings|profile|accounts|submissions|analytics|statistics|login|logout)(\/|$)/i,
  /\/(edit|new)(\/|$)/i,
];

/** Content areas: user-authored HTML inside #content only (never nav, sidebar, editors) */
export const CANVAS_SELECTORS: string[] = [
  '#content .show-content.user_content',            // wiki pages / course home page
  '#content #course_syllabus',                      // syllabus
  '#content .description.user_content',             // assignment & quiz descriptions
  '#content #quiz-instructions',                    // classic quiz instructions
  '#content .question_text.user_content',           // classic quiz question text
  '#content .discussion-section .message.user_content', // classic discussion topic / announcement
  '#content .discussion_entry .message.user_content',   // classic discussion replies
  '#content .ic-announcement-row__content',         // announcements list
  '#content .user_content',                         // new (React) discussions, replies, misc user HTML
];

export const CANVAS_IGNORE_SELECTORS: string[] = [
  'script', 'style', 'noscript', 'iframe', 'object', 'embed',
  '[contenteditable]', '.tox', '.tox-tinymce', '.mce-tinymce', '.ic-RichContentEditor', '.rce-wrapper',
  'textarea', 'input', 'select', 'button', '[role="textbox"]', '[role="toolbar"]', '[role="menu"]',
  '.screenreader-only', '.ui-helper-hidden-accessible', '[aria-hidden="true"]', '[hidden]',
  '.admin-links', '.al-options', '.ig-admin', '.discussion-reply-action', '.discussion-entry-reply-area',
  'nav', 'footer', '.no-read',
];

/** Elements a content block must never be inside (editors, toolbars) */
export const CANVAS_BLOCK_EXCLUDE = '[contenteditable], [role="textbox"], .tox, .tox-tinymce, .mce-tinymce, .ic-RichContentEditor, .rce-wrapper, #left-side, #right-side, #header, .ic-app-header, .ic-app-nav-toggle-and-crumbs, nav';

/**
 * Moodle pages where the widget must not activate: gradebook / grader reports,
 * messaging, user profiles & participants, site admin, login, badges, calendar,
 * reports, enrolment, question bank, backup/restore, and course/activity
 * editing & grading pages. Matched against location.pathname (Moodle may live
 * in a subdirectory, so segments are matched anywhere in the path).
 */
export const MOODLE_EXCLUDED_PATHS: RegExp[] = [
  /\/(grade|message|user|admin|login|badges|calendar|report|reportbuilder|enrol|cohort|question|backup|competency|auth)(\/|$)/i,
  /\/course\/(edit|modedit|admin|completion|user|reset|bulkcompletion|editsection|management|defaultcompletion)\.php/i,
  /\/mod\/[^/]+\/(edit|grade|grading|report|overrides|submissions)[^/]*$/i,
];

/**
 * Content areas inside Moodle's main region only (Boost / Classic 4.x; most
 * also exist in 3.x). Nested matches are dropped, so a course section wins
 * over the activity descriptions inside it.
 */
export const MOODLE_SELECTORS: string[] = [
  '#region-main .course-content li.section',                   // course page sections (summary, labels, activities)
  '#region-main .course-content .course-section',              // 4.4+ section markup
  '#region-main .activity-description',                        // activity intro (4.x activity header)
  '#region-main .box.generalbox',                              // page / book / assignment / quiz intro content
  '#region-main .forumpost .post-content-container',           // forum post body (4.x)
  '#region-main [data-region-content="forum-post-core"] .post-content-container',
  '#region-main .forumpost .posting',                          // forum post body (3.x / classic)
  '#region-main .qtext',                                       // quiz question text
  '#region-main .no-overflow',                                 // other formatted user content
];

export const MOODLE_IGNORE_SELECTORS: string[] = [
  'script', 'style', 'noscript', 'iframe', 'object', 'embed', 'form',
  '[contenteditable]', '.editor_atto', '.editor_atto_wrap', '.tox', '.tox-tinymce', '.mce-tinymce',
  'textarea', 'input', 'select', 'button', '[role="textbox"]', '[role="toolbar"]', '[role="menu"]',
  '.accesshide', '.sr-only', '.visually-hidden', '[aria-hidden="true"]', '[hidden]',
  '.action-menu', '.dropdown-menu', '.moodle-actionmenu', '.section-actions', '.section_action_menu',
  '.activity-actions', '.editing_move', '.section-modchooser', '.activity-add', '.add-sections', '.changenumsections',
  '.completion-info', '[data-region="completion-info"]', '.activity-completion', '.automatic-completion-conditions',
  '.activity-navigation', '.forum-post-footer', '.post-actions', '.commands',
  'nav', 'footer', '.no-read',
];

/** Elements a content block must never be inside (navigation, drawers, blocks, editors, forms) */
export const MOODLE_BLOCK_EXCLUDE = '[contenteditable], [role="textbox"], .editor_atto, .editor_atto_wrap, .tox, .tox-tinymce, .mce-tinymce, form.mform, .navbar, #nav-drawer, .drawer, .secondary-navigation, .breadcrumb, [data-region="blocks-column"], .block-region, .block, #page-footer, #page-header, nav';

/** Config applied by data-preset="canvas" / "moodle" (explicit config values still win) */
export function presetConfig(id: PresetId): Partial<ReadingVoxConfig> {
  switch (id) {
    case 'canvas':
      return {
        selectors: CANVAS_SELECTORS,
        ignoreSelectors: CANVAS_IGNORE_SELECTORS,
        excludePaths: CANVAS_EXCLUDED_PATHS,
        blockExclude: CANVAS_BLOCK_EXCLUDE,
        contentOnly: true,
        observeContent: true,
        stripUrlParams: true,
        // Canvas global nav is a fixed ~84px column on the left; bottom-right is free
        togglePosition: 'bottom-right',
        toolbarPosition: 'bottom',
        toolbarInsetLeft: 84,
      };
    case 'moodle':
      return {
        selectors: MOODLE_SELECTORS,
        ignoreSelectors: MOODLE_IGNORE_SELECTORS,
        excludePaths: MOODLE_EXCLUDED_PATHS,
        blockExclude: MOODLE_BLOCK_EXCLUDE,
        contentOnly: true,
        observeContent: true,
        stripUrlParams: true,
        // Moodle 4 keeps a fixed "?" footer button at bottom-right (bottom/right
        // 2rem, ~40px) and opens the course index drawer on the LEFT by default,
        // so bottom-left would sit over the course index. Stay bottom-right but
        // stack the toggle above the "?" button, and keep the bottom toolbar
        // clear of it.
        togglePosition: 'bottom-right',
        toggleBottomOffset: 100,
        toolbarPosition: 'bottom',
        toolbarInsetRight: 88,
      };
  }
}

/** Page URL as sent to the API: optionally without ?query and #hash */
export function pageUrlForApi(strip: boolean): string {
  const href = window.location.href;
  if (!strip) return href;
  try {
    const u = new URL(href);
    return u.origin + u.pathname;
  } catch {
    return href.split(/[?#]/)[0];
  }
}
